
import { nanoid } from 'nanoid';
import { Component, Ref, Vue } from 'vue-property-decorator';
import { InputValidationRule } from 'vuetify';

import AsideLabel from '../components/AsideLabel.vue';
import ErrorAlert from 'spa-ionata-admin/lib/components/ErrorAlert.vue';
import AutoDismissedError from 'spa-ionata-admin/lib/services/AutoDismissedError';
import keys from 'spa-ionata-admin/lib/utils/keys';
import { emailRules, emptyCheck } from 'spa-ionata-admin/lib/utils/validators';
import ObservedPromise from 'spa-ionata-admin/lib/services/ObservedPromise';
import DialogStepper, { StepperStep } from '../components/DialogStepper.vue';
import { Choice, StepItem } from '../components/FormItem.vue';
import FormItems from '../components/FormItems.vue';
// import ScrollMap from '../components/ScrollMap.vue';
import BusinessLead from '../models/BusinessLead';
import { exportMarketsModule } from '../store';

type FormStep = StepperStep & {
  items?: StepItem[];
  itemsKey?: () => string;
  nextButton?: string;
};

@Component({
  components: {
    DialogStepper,
    FormItems,
    AsideLabel,
    ErrorAlert,
    // ScrollMap,
  },
})
export default class extends Vue {
  @Ref() readonly form!: VuetifyForm;

  @Ref() readonly stepper!: DialogStepper;

  version = '1.1.0';

  data = {
    // eligibility - step 1
    has_business_plan: null,
    has_export_strategy: null,
    has_committed_time: null,
    has_strong_sales: null,
    has_resources: null,
    is_export_ready: null,
    is_exporting_already: null,
    is_somewhat_reliant: null,
    is_heavily_reliant: null,
    // eligibility - step 2
    sector: null,
    // eligibility - step 3
    export_markets: null as string[] | null,
    // eligibility - step 4
    first_name: null,
    last_name: null,
    email: null,
    phone: null,
    business_name: null,
  };

  currentStep = 1;

  dataError: Error | null = null;

  showError = true;

  result = new ObservedPromise<boolean>();

  lead = new BusinessLead();

  get applicationId() {
    const { id } = this.$route.query;
    if (typeof id === 'string') {
      return id;
    }
    return undefined;
  }

  get steps(): FormStep[] {
    return [
      this.checkStep,
      this.industryStep,
      this.exportMarketsStep,
      this.informationStep,
      this.outcomeStep,
      this.finalStep,
    ];
  }

  get checkStep(): FormStep {
    // NOTE: try to NOT use state directly, so this doesn't need to be re-computed
    return {
      title: `${this.$t('eligibility.check_title')}`,
      items: [
        {
          label: `${this.$t('eligibility.check_label')}`,
          labelClass: 'h4',
        },
        {
          label: `${this.$t('eligibility.check_business_plan')}`,
          key: 'has_business_plan',
          rules: this.requiredRules,
          type: 'boolean',
        },
        {
          label: `${this.$t('eligibility.check_export_strategy')}`,
          key: 'has_export_strategy',
          rules: this.requiredRules,
          type: 'boolean',
        },
        {
          label: `${this.$t('eligibility.check_committed_time')}`,
          key: 'has_committed_time',
          rules: this.requiredRules,
          type: 'boolean',
        },
        {
          label: `${this.$t('eligibility.check_market_sales')}`,
          key: 'has_strong_sales',
          rules: this.requiredRules,
          type: 'boolean',
        },
        {
          label: `${this.$t('eligibility.check_resources_to_succeed')}`,
          key: 'has_resources',
          rules: this.requiredRules,
          type: 'boolean',
        },
        {
          label: `${this.$t('eligibility.check_export_ready')}`,
          key: 'is_export_ready',
          rules: this.requiredRules,
          type: 'boolean',
        },
        {
          label: `${this.$t('eligibility.check_exporting_already')}`,
          key: 'is_exporting_already',
          rules: this.requiredRules,
          type: 'boolean',
        },
        {
          label: `${this.$t('eligibility.check_somewhat_relies')}`,
          key: 'is_somewhat_reliant',
          rules: this.requiredRules,
          type: 'boolean',
        },
        {
          label: `${this.$t('eligibility.check_heavily_relies')}`,
          key: 'is_heavily_reliant',
          rules: this.requiredRules,
          type: 'boolean',
        },
      ],
      complete: () => {
        const { data } = this;
        const check = {
          has_business_plan: this.requiredRules,
          has_export_strategy: this.requiredRules,
          has_committed_time: this.requiredRules,
          has_strong_sales: this.requiredRules,
          has_resources: this.requiredRules,
          is_export_ready: this.requiredRules,
          is_exporting_already: this.requiredRules,
          is_somewhat_reliant: this.requiredRules,
          is_heavily_reliant: this.requiredRules,
        };
        return keys(check).every(key => this.checkRules(check[key], data[key]));
      },
      disabled: () => !this.applicationId || this.loading || this.success,
    };
  }

  get industryStep(): FormStep {
    // NOTE: try to NOT use state directly, so this doesn't need to be re-computed
    return {
      title: `${this.$t('eligibility.sector_title')}`,
      items: [
        {
          label: `${this.$t('eligibility.sector_label')}`,
          labelClass: ['h4'],
          key: 'sector',
          rules: this.requiredRules,
          choices: [
            {
              label: `${this.$t('eligibility.sector_science_and_technology')}`,
              value: 'science_and_technology',
            },
            {
              label: `${this.$t('eligibility.sector_advanced_manufacturing')}`,
              value: 'advanced_manufacturing',
            },
            {
              label: `${this.$t('eligibility.sector_food_and_agribusiness')}`,
              value: 'food_and_agribusiness',
            },
            {
              label: `${this.$t('eligibility.sector_maritime')}`,
              value: 'maritime',
            },
            {
              label: `${this.$t(
                'eligibility.sector_my_business_aligns_to_another_sector',
              )}`,
              value: 'my_business_aligns_to_another_sector',
            },
          ],
        },
      ],
      complete: () => {
        const { data } = this;
        const check = {
          sector: this.requiredRules,
        };
        return keys(check).every(key => this.checkRules(check[key], data[key]));
      },
      disabled: () => !this.applicationId || this.loading || this.success,
    };
  }

  get exportMarketsStep(): FormStep {
    // NOTE: try to NOT use state directly, so this doesn't need to be re-computed
    return {
      title: `${this.$t('eligibility.export_markets_title')}`,
      items: [
        {
          label: `${this.$t('eligibility.export_markets_label')}`,
          labelClass: ['h4'],
          key: 'export_markets',
          multiChoice: true,
          maxChoices: 3,
          choices: exportMarketsModule.items.map<Choice>(item => ({
            label: item.locale.title || item.title.en,
            value: item.shortCode,
          })),
        },
      ],
      complete: () => {
        const { data } = this;
        const check = {
          export_markets: [
            (v: string[] | null) => (v && v.length <= 3) || 'error',
          ],
        };
        return keys(check).every(key => this.checkRules(check[key], data[key]));
      },
      disabled: () => !this.applicationId || this.loading || this.success,
    };
  }

  get informationStep(): FormStep {
    // NOTE: try to NOT use state directly, so this doesn't need to be re-computed
    return {
      title: `${this.$t('eligibility.information_title')}`,
      separator: false,
      items: [
        {
          label: `${this.$t('eligibility.information_label')}`,
          labelClass: 'h4',
          description: `${this.$t('eligibility.information_description')}`,
        },
        {
          type: 'group',
          md: 6,
          items: [
            {
              label: `${this.$t('eligibility.first_name_label')}*`,
              key: 'first_name',
              input: 'text',
              rules: this.requiredRules,
            },
            {
              label: `${this.$t('eligibility.last_name_label')}*`,
              key: 'last_name',
              input: 'text',
              rules: this.requiredRules,
            },
          ],
        },
        {
          type: 'group',
          md: 6,
          items: [
            {
              label: `${this.$t('eligibility.business_name_label')}*`,
              key: 'business_name',
              input: 'text',
              rules: this.requiredRules,
            },
            {
              label: `${this.$t('eligibility.email_label')}*`,
              key: 'email',
              input: 'email',
              rules: this.emailRules,
            },
          ],
        },
        {
          type: 'group',
          md: 6,
          items: [
            {
              label: `${this.$t('eligibility.phone_label')}*`,
              key: 'phone',
              input: 'tel',
              rules: this.requiredRules,
            },
          ],
        },
        {
          label: `${this.$t('general.required_fields_description')}`,
          labelClass: 'caption',
        },
      ],
      complete: () => {
        const { data } = this;
        const check = {
          first_name: this.requiredRules,
          last_name: this.requiredRules,
          business_name: this.requiredRules,
          email: this.emailRules,
          phone: this.requiredRules,
        };
        return keys(check).every(key => this.checkRules(check[key], data[key]));
      },
      disabled: () => !this.applicationId || this.loading || this.success,
    };
  }

  get outcomeStep(): FormStep & {
    eligible: StepItem[];
    ineligible: StepItem[];
  } {
    // NOTE: try to NOT use state directly, so this doesn't need to be re-computed
    return {
      key: 'outcome',
      title: `${this.$t('eligibility.outcome_title')}`,
      separator: false,
      itemsKey: () => {
        return this.isEligible ? 'eligible' : 'ineligible';
      },
      eligible: [
        {
          label: `${this.$t('eligibility.eligible_title')}`,
          labelClass: 'h4',
          description: `${this.$t('eligibility.eligible_content')}`.split('\n'),
        },
      ],
      ineligible: [
        {
          label: `${this.$t('eligibility.ineligible_title')}`,
          labelClass: 'h4',
          description: `${this.$t('eligibility.ineligible_content')}`.split(
            '\n',
          ),
        },
        {
          type: 'group',
          md: 6,
          items: [
            {
              label: `${this.$t('eligibility.first_name_label')}*`,
              key: 'first_name',
              input: 'text',
              rules: this.requiredRules,
            },
            {
              label: `${this.$t('eligibility.last_name_label')}*`,
              key: 'last_name',
              input: 'text',
              rules: this.requiredRules,
            },
          ],
        },
        {
          type: 'group',
          md: 6,
          items: [
            {
              label: `${this.$t('eligibility.business_name_label')}*`,
              key: 'business_name',
              input: 'text',
              rules: this.requiredRules,
            },
            {
              label: `${this.$t('eligibility.email_label')}*`,
              key: 'email',
              input: 'email',
              rules: this.emailRules,
            },
          ],
        },
        {
          type: 'group',
          md: 6,
          items: [
            {
              label: `${this.$t('eligibility.phone_label')}*`,
              key: 'phone',
              input: 'tel',
              rules: this.requiredRules,
            },
          ],
        },
        {
          type: 'textarea',
          label: `${this.$t('eligibility.message_label')}`,
          key: 'message',
          // useExternalLabel: true,
        },
        {
          label: `${this.$t('general.required_fields_description')}`,
          labelClass: 'caption',
        },
      ],
      disabled: () => !this.applicationId || this.loading || this.success,
      nextButton: `${this.$t('general.submit_button_label')}`,
    };
  }

  get finalStep(): FormStep {
    // NOTE: try to NOT use state directly, so this doesn't need to be re-computed
    return {
      key: 'final',
      indexText: ' ',
      navTitle: `${this.$t('application.final_nav')}`,
      title: `${this.$t('application.final_title')}`,
      itemsKey: () => {
        return this.isEligible ? 'eligible' : 'ineligible';
      },
      eligible: [
        {
          label: `${this.$t('application.final_label_eligible')}`,
          labelClass: 'h4',
          description: `${this.$t(
            'application.final_description_eligible',
          )}`.split('\n'),
        },
      ],
      ineligible: [
        {
          label: `${this.$t('application.final_label_ineligible')}`,
          labelClass: 'h4',
          description: `${this.$t(
            'application.final_description_ineligible',
          )}`.split('\n'),
        },
      ],
      disabled: () => !this.applicationId || this.loading || !this.success,
    };
  }

  get requiredRules(): InputValidationRule[] {
    const message = this.$t('general.required_field') as string;
    return [(v: string) => emptyCheck(v) || message];
  }

  get emailRules(): InputValidationRule[] {
    const translator = (t: string) => this.$t(`general.${t}`) as string;
    return emailRules(translator);
  }

  get isEligible(): boolean {
    const [step1, step2, step3] = this.steps;
    if (
      !this.isComplete(step1) ||
      !this.isComplete(step2) ||
      !this.isComplete(step3)
    ) {
      return false;
    }

    const { data } = this;
    const check = {
      has_business_plan: 'yes',
      has_export_strategy: 'yes',
      has_committed_time: 'yes',
      has_strong_sales: 'yes',
      has_resources: 'yes',
      is_export_ready: 'yes',
      is_exporting_already: 'yes',
      is_somewhat_reliant: 'yes',
      is_heavily_reliant: 'yes',
    };
    const yesCount = keys(check).reduce(
      (agg, key) => agg + (data[key] === check[key] ? 1 : 0),
      0,
    );
    return yesCount >= 4;
  }

  get loading() {
    return this.result.isPending;
  }

  get success() {
    return this.result.isFulfilled && this.result.value === true;
  }

  get error(): Error | null {
    return this.result.error || this.dataError;
  }

  set error(value: Error | null) {
    this.showError = !!value;
  }

  onSubmit() {
    try {
      this.saveData();
    } catch (e) {
      // ignore error
    }
    if (this.form.validate()) {
      if (this.stepper.stepObject?.key === 'outcome') {
        if (this.isEligible) {
          this.submitApplication();
        } else {
          this.submitDetails();
        }
      } else {
        this.stepper.gotoNext();
      }
    }
  }

  submitApplication() {
    const { version } = this;
    const {
      first_name: firstName,
      last_name: lastName,
      email,
      phone,
      business_name: businessName,
      ...rest
    } = this.data;
    const promise = new Promise<boolean>(resolve => {
      const lead = new BusinessLead({
        id: null,
        firstName,
        lastName,
        email,
        phone,
        businessName,
        content: {
          ...rest,
          version,
        },
      });
      resolve(this.saveLead(lead));
    });
    this.result = new ObservedPromise(promise);
    this.showError = true;
  }

  async saveLead(item: BusinessLead): Promise<boolean> {
    this.lead = item;

    const result = await item.save({
      with: [],
    });

    this.stepper.gotoNext();

    if (this.applicationId) {
      sessionStorage.removeItem(this.applicationId);
      this.$router.replace({ query: {} });
    }

    return result;
  }

  onStepChanged() {
    window.location.href = '#dialog-stepper--content';
  }

  isComplete(step: StepperStep): boolean {
    if (step.complete) {
      return !!step.complete();
    }
    return true;
  }

  checkRules<T>(rules: InputValidationRule[], value: T | null): boolean {
    return rules.every(r => r(value) === true);
  }

  submitDetails() {
    window.alert('WIP');
  }

  saveData() {
    const { applicationId: id, data, currentStep } = this;
    if (!id) {
      return;
    }
    const serialized = JSON.stringify({ currentStep, data });
    sessionStorage.setItem(id, serialized);
  }

  loadData() {
    const { applicationId: id } = this;
    if (!id) {
      return;
    }
    const serialized = sessionStorage.getItem(id);
    if (serialized) {
      const obj = JSON.parse(serialized);
      if (
        typeof obj === 'object' &&
        typeof obj.data === 'object' &&
        !Array.isArray(obj.data) &&
        typeof obj.currentStep === 'number'
      ) {
        this.data = obj.data;
        const lastComplete = this.steps.findIndex(s =>
          s.complete ? !s.complete() : false,
        );
        if (lastComplete !== -1 && lastComplete + 1 >= obj.currentStep) {
          this.currentStep = obj.currentStep;
        }
      }
    }
  }

  mounted() {
    if (!this.applicationId) {
      const id = `apl-${nanoid()}`;
      this.$router.replace({ query: { id } });
    } else {
      exportMarketsModule.fetchItemsIfNeeded();
      try {
        this.loadData();
      } catch (e) {
        if (e instanceof SyntaxError) {
          this.dataError = new AutoDismissedError(
            `${this.$t('eligibility.unable_to_load')}`,
          );
        } else {
          this.dataError = e;
        }
      }
    }
  }
}
